import { DeepPartial, Roles } from "@arelia-types/index";
import { FormikProps, useFormik } from "formik";
import { useParams, useSearchParams } from "react-router-dom";
import { CustomerDetails } from "src/features/hq/customers/models";
import { useHandleChange } from "@arelia-hooks/formikHooks";
import CustomerInfoPresenter from "./CustomerInfoPresenter";
import { useTwoFactorStatus, useResetTwoFactor } from "./api";

type Props = {
  customerDetails: CustomerDetails;
  updateCustomer: (cutomerUpdates: DeepPartial<CustomerDetails>) => void;
};

export type EditForm = {
  labelOrManager: string;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  initialPassword: string;
  leadSource: string;
  notes: string;
  role: Roles;
};

export default function CustomerInfoContainer({ customerDetails, updateCustomer }: Props) {
  const [searchParams] = useSearchParams();
  const { id: identityId } = useParams<{ id: string }>();
  const { data: twoFaStatus } = useTwoFactorStatus(identityId);
  const resetTwoFa = useResetTwoFactor(identityId);

  const onReset2FA = async () => {
    if (!identityId) return;
    await resetTwoFa.mutateAsync();
  };

  const mapper = ({ address, country, email, fullName, initialPassword, labelOrManager, leadSource, notes, phone, role }: EditForm) => ({
    accountInfo: { email, fullName, phone, address: `${address},&#160 ${country}`, role },
    band: { labelOrManager },
    customerInfo: { leadSource, notes, initialPassword }
  } as DeepPartial<CustomerDetails>);

  const submitUpdates = (updates: EditForm) => updateCustomer(mapper(updates));

  const [address, country] = (customerDetails.address || '').split(",&#160");
  const formik: FormikProps<EditForm> = useFormik<EditForm>({
    initialValues: {
      address,
      country: country?.trim(),
      email: customerDetails.email,
      fullName: customerDetails.fullName,
      initialPassword: customerDetails.customerInfo?.initialPassword,
      labelOrManager: customerDetails.band?.labelOrManager,
      leadSource: customerDetails.customerInfo?.leadSource,
      notes: customerDetails.customerInfo?.notes,
      phone: customerDetails.phone,
      role: customerDetails.role,
    },
    onSubmit: submitUpdates,
    enableReinitialize: true,
  });

  const handeChange = useHandleChange<EditForm>(formik);

  return (
    <CustomerInfoPresenter
      formik={formik}
      initEditing={searchParams.get("edit") === "true"}
      handeChange={handeChange}
      onReset2FA={onReset2FA}
      twoFactorEnabled={!!twoFaStatus?.enabled}
    />
  );
}
